import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

export const LOADING_MESSAGE_INTERVAL_MS = 1150;

// Five bars regardless of how many colors are passed, so the mark stays
// recognisable whether the caller has one series or four.
const BAR_COUNT = 5;
const FALLBACK_BAR_COLOR = "#087069";

interface LoadingOverlayProps {
  open: boolean;
  /** Stable line announced by the live region. */
  title: string;
  /**
   * Rotated under the title. Name what the wait is actually doing rather than
   * a generic "Loading…" — callers narrate their real pipeline stages.
   */
  messages: string[];
  /** Bar hues, cycled across the five bars; empty falls back to brand teal. */
  barColors: string[];
  progressAriaLabel: string;
  /**
   * Pin the card `position: sticky` when the scrim spans a data region several
   * screens tall (marketing dashboard) — centring would put the card below
   * the fold. Off, the card top-aligns inside a viewport-sized scrim.
   */
  sticky?: boolean;
  maxWidth?: number;
  "data-testid"?: string;
}

function useRotatingMessage(open: boolean, messages: string[]) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!open) {
      setIndex(0);
      return undefined;
    }
    const timer = setInterval(
      () => setIndex((current) => (current + 1) % messages.length),
      LOADING_MESSAGE_INTERVAL_MS
    );
    return () => clearInterval(timer);
  }, [open, messages.length]);

  return messages[index] ?? messages[0];
}

/**
 * Full-surface loading overlay — building-bars mark, stable live-region title,
 * rotating aria-hidden caption, indeterminate progress. Absolutely positioned,
 * so it must be mounted inside the caller's own positioned container: it dims
 * the data without covering app chrome or the control that triggered it.
 *
 * Deliberately an overlay rather than in-place skeletons: on a refetch the
 * numbers underneath are still the *previous* result, and dimming them is what
 * stops someone reading a stale figure as the new one.
 *
 * Visibility is gated by `useDeferredLoading` in the parent (show delay + min
 * visible) — this component never decides when to appear, only what appears.
 */
export default function LoadingOverlay({
  open,
  title,
  messages,
  barColors,
  progressAriaLabel,
  sticky = false,
  maxWidth = 360,
  "data-testid": dataTestId
}: LoadingOverlayProps) {
  const message = useRotatingMessage(open, messages);

  const cycle = barColors.length > 0 ? barColors : [FALLBACK_BAR_COLOR];
  const bars = Array.from(
    { length: BAR_COUNT },
    (_, position) => cycle[position % cycle.length]
  );

  const card = (
    <Paper
      elevation={8}
      sx={{
        px: 3.25,
        py: 2.75,
        borderRadius: 2.5,
        width: `min(${maxWidth}px, calc(100% - 32px))`,
        textAlign: "center"
      }}
    >
      <Box
        aria-hidden
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          gap: 0.75,
          height: 34,
          mb: 1.75
        }}
      >
        {bars.map((color, position) => (
          <Box
            key={`${color}-${position}`}
            sx={{
              width: 7,
              height: "26%",
              borderRadius: 1,
              bgcolor: color,
              opacity: 0.45,
              animation: "loadingOverlayBuild 1.15s ease-in-out infinite",
              animationDelay: `${position * 0.1}s`,
              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
                height: "70%",
                opacity: 0.8
              }
            }}
          />
        ))}
      </Box>

      <Typography variant="subtitle2" fontWeight={700}>
        {title}
      </Typography>

      {/* aria-hidden: the wrapper is a polite live region and this line
          changes every ~1.2s — announcing each rotation is chatter; the
          stable title above is the useful announcement. */}
      <Typography
        aria-hidden
        variant="caption"
        color="text.secondary"
        component="p"
        sx={{
          mt: 0.5,
          mb: 2,
          minHeight: "2.6em",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        {message}
      </Typography>

      <LinearProgress
        sx={{ height: 4, borderRadius: 2 }}
        aria-label={progressAriaLabel}
      />
    </Paper>
  );

  return (
    <Fade in={open} timeout={{ enter: 180, exit: 140 }} unmountOnExit>
      <Box
        role="status"
        aria-live="polite"
        data-testid={dataTestId}
        sx={{
          position: "absolute",
          inset: 0,
          // Only needs to beat its siblings inside the caller's container. A
          // modal-tier z-index would also paint over app chrome that overlaps
          // this region on scroll, and buys nothing.
          zIndex: 3,
          backdropFilter: "blur(2px)",
          bgcolor: (theme) => alpha(theme.palette.background.default, 0.72),
          // Keyframes are global once injected; declaring them here rather
          // than on each bar avoids emitting five identical copies (one per
          // delay-varied child class).
          "@keyframes loadingOverlayBuild": {
            "0%, 100%": { height: "26%", opacity: 0.45 },
            "50%": { height: "100%", opacity: 1 }
          },
          ...(sticky
            ? {}
            : {
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                pt: 5
              })
        }}
      >
        {sticky ? (
          /* The scrim can span a data region several screens tall — centring
             the card inside it puts the card below the fold on a laptop.
             Sticky keeps it in the visible part of the scroll container at
             any scroll position. `top: 0` plus padding, rather than a `top`
             offset, so the gap above the card is the same stuck or unstuck. */
          <Box
            sx={{
              position: "sticky",
              top: 0,
              display: "flex",
              justifyContent: "center",
              pt: { xs: 4, md: 10 },
              pb: 4
            }}
          >
            {card}
          </Box>
        ) : (
          card
        )}
      </Box>
    </Fade>
  );
}
